import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { visitsApiRef } from '@backstage/plugin-home';

/**
 * Records page visits once per pathname change.
 *
 * Upstream VisitListener recreates callback deps every render, which can
 * re-save the same path. `/` is skipped because RootRedirect replaces it
 * with the preferred landing page.
 */
export const StableVisitListener = () => {
  const visitsApi = useApi(visitsApiRef);
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === '/') {
      return undefined;
    }

    const requestId = requestAnimationFrame(() => {
      const entityMatch = pathname.match(
        /^\/catalog\/(?<namespace>[^/]+)\/(?<kind>[^/]+)\/(?<name>[^/]+)/,
      );
      const entityRef = entityMatch?.groups
        ? stringifyEntityRef({
            namespace: entityMatch.groups.namespace,
            kind: entityMatch.groups.kind,
            name: entityMatch.groups.name,
          })
        : undefined;
      const name =
        entityMatch?.groups?.name ??
        pathname.match(/^\/(?<name>[^/]+)$/)?.groups?.name ??
        document.title;

      visitsApi.save({
        visit: {
          name,
          pathname,
          entityRef,
        },
      }).catch(() => {
        // Visit tracking failures are non-critical; suppress to avoid unhandled rejections
      });
    });

    return () => cancelAnimationFrame(requestId);
  }, [visitsApi, pathname]);

  return null;
};
